/*
 * @copyright Copyright (c) valantic CEC Deutschland GmbH (https://www.valantic.com)
 * @see PROJECT_LICENSE.txt
 */

import { v4 as uuidv4 } from 'uuid'
import { QUICK_SEARCH_QUERY } from '../api/queries'
import { productSearch, getGenderFilter, executeGenderFallbackSearch } from '../api/request'

const topSearchRequest = {
    /**
     * get live search products
     *
     * @returns {Promise<*|*[]>}
     */
    async getTopSearchProducts() {
        try {
            this.searchRequestId = uuidv4()
            this.liveSearchError = null

            this.handleContextInput()

            const before = Date.now()

            const genderOutcome = await executeGenderFallbackSearch(async(genderKey) => {
                const searchParams = {
                    phrase: this.queryPhrase,
                    pageSize: this.topSearchPageSize,
                    currentPage: 1,
                    filter: [
                        { attribute: 'visibility', in: ['Catalog, Search'] },
                        { attribute: 'inStock', eq: 'true' },
                        { attribute: 'department', eq: getGenderFilter(null, genderKey) }
                    ]
                }

                return await productSearch(
                    this.apiUrl,
                    QUICK_SEARCH_QUERY,
                    searchParams,
                    (error) => this.handleTopSearchFetchError(error)
                )
            })

            const result = genderOutcome.response ?? this.getEmptyTopSearchResponse()

            const after = Date.now()

            this.handleContextResults(result, before, after)
            this.updateSuggestionsHeading(genderOutcome)

            return result
        } catch (error) {
            console.error('getTopSearchProducts error:', error)
            this.resetSuggestionsHeading()
            return this.getEmptyTopSearchResponse()
        }
    },
    /**
     * get products
     *
     * @returns {Promise<void>}
     */
    async getProducts() {
        try {
            const result = await this.getTopSearchProducts()

            this.liveSearchProducts = this.formatProductItems(result.productSearch.items)
            this.liveSearchSuggestions = result.productSearch.suggestions
            this.liveSearchCategories = this.getCategories(result.productSearch.facets)

            this.dataLayerPushData(this.liveSearchProducts, this.liveSearchSuggestions, this.queryPhrase)
        } catch (error) {
            console.error('getProducts error:', error)
            if (!this.liveSearchError) {
                this.handleTopSearchFetchError(error)
            }
        }
    },
    /**
     * handle fetch errors for top search
     *
     * @param {Error} error
     */
    handleTopSearchFetchError(error) {
        console.error('Top search Live Search error:', error)
        this.liveSearchError = this.errorMessages.productFetchError
        this.liveSearchProducts = []
        this.liveSearchSuggestions = []
        this.liveSearchCategories = []
    },
    /**
     * retry the last top search request
     *
     * @returns {Promise<void>}
     */
    async retryTopSearch() {
        if (!this.queryPhrase || this.isLoading) {
            return
        }

        await this.searchInputCallback()
    },
    /**
     * Provide empty response shape to keep UI stable
     *
     * @returns {{productSearch: {suggestions: *[], items: *[], facets: *[], page_info: {page_size: *, current_page: number}}}}
     */
    getEmptyTopSearchResponse() {
        return {
            productSearch: {
                suggestions: [],
                items: [],
                facets: [],
                page_info: {
                    current_page: 1,
                    page_size: this.topSearchPageSize
                }
            }
        }
    }
}

export default topSearchRequest
